import { FC, useEffect, useMemo } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { FolderIcon, Pencil } from "lucide-react";
import { useForm } from "react-hook-form";
import Workspace from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";
import {
  buildRenameFolderSchema,
  getSiblingFolderNames,
  RenameFolderFormValues,
} from "@/pages/workspace/utils/folderRename";

export type RenameFolderDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace | null;
  item: Item | null;
  /** Called with the trimmed new name. Only invoked when the name actually changed. */
  onRename: (name: string) => Promise<void>;
  isPending: boolean;
};

const RenameFolderDialog: FC<RenameFolderDialogProps> = ({
  isOpen,
  onClose,
  workspace,
  item,
  onRename,
  isPending,
}) => {
  const siblingNames = useMemo(
    () => getSiblingFolderNames(workspace, item?.id),
    [workspace, item?.id]
  );

  // react-hook-form re-reads `resolver` on every render, so the schema always
  // validates against the sibling list as the workspace looks now — including
  // the first render after a folder is picked from the dropdown (LAR-397).
  const form = useForm<RenameFolderFormValues>({
    resolver: zodResolver(buildRenameFolderSchema(siblingNames)),
    defaultValues: { name: item?.name ?? "" },
  });

  // Re-seed the field whenever a different folder is picked from the menu — the
  // dialog stays mounted, so the previous folder's name would otherwise stick.
  useEffect(() => {
    if (isOpen) form.reset({ name: item?.name ?? "" });
  }, [isOpen, item?.id, item?.name, form]);

  const handleSubmit = form.handleSubmit(async ({ name }) => {
    const trimmed = name.trim();
    if (!item || trimmed === item.name) {
      onClose();
      return;
    }

    try {
      await onRename(trimmed);
      onClose();
    } catch {
      // onRename reports the failure; keep the dialog open so the name is not lost.
    }
  });

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
      isDirty={form.formState.isDirty}
      size="md"
    >
      <DialogHeader className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-full">
            <FolderIcon className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-xl">Omdøb mappe</DialogTitle>
        </div>
        <DialogDescription className="text-sm text-muted-foreground">
          Giv mappen et nyt navn. Indholdet bliver ikke ændret.
        </DialogDescription>
      </DialogHeader>

      <Separator className="my-1" />

      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  <Pencil className="h-3.5 w-3.5 text-primary" />
                  Mappe navn
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="F.eks. NDAer"
                      className="pl-9"
                      autoFocus
                      data-testid="rename-folder-name"
                    />
                  </FormControl>
                  <FolderIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="gap-2 space-x-2 sm:gap-0 mt-6">
            <FormDialogCancel disabled={isPending} />
            <Button
              type="submit"
              className="gap-2 min-w-[100px]"
              disabled={isPending}
            >
              {isPending ? (
                <Spinner className="h-4 w-4 text-primary-foreground" />
              ) : (
                <span className="flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  Gem
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default RenameFolderDialog;
